import React, { useEffect, useRef, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/Components/ui/form'
import { Input } from '@/Components/ui/input'
import { Button } from '@/Components/ui/button'
import { useLoaderData, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import BodyCard from '@/Components/BodyCard'
import HeaderCard from '@/Components/HeaderCard'
import UserService from '@/Service/UserService'
import { Eye, EyeClosed } from 'lucide-react'
import { Checkbox } from '@/Components/ui/checkbox'
import WMSSettingsService from '@/Service/WMSSettingsService'
import Loader from '@/Components/Common/Loader'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Components/ui/tabs'
import Overview from './FormTabs/Overview'
import Coding from './FormTabs/Coding'
import { useReducer } from 'react'
import Oasis from './FormTabs/Oasis'
import CodingPostProblem from './FormTabs/CodingPostProblem'
import Followupmail from './FormTabs/Followupmail'
import WorkAllocationFormService from '@/Service/WorkAllocationFormService'
import { useDebounce } from 'use-debounce'
import Constant from '@/utils/Constant'
import { useAuth } from '@/Context/AuthContext'
import ActionButton from './ActionButton'

const WorkAllocationForm = () => {
    const { userInfo, actionButton } = useAuth()

    const [isSaving, setIsSaving] = useState(false)
    const [currentTab, setCurrentTab] = useState('Overview')
    const formRef = useRef(null)
    const [action, setAction] = useState([])
    const { id } = useParams()
    const { technicalIssue, holdReason, chartLockIssue, category, subCategory, followupStatus, workflowData } = useLoaderData()

    const handleSaveClick = () => {
        if (formRef.current) {
            formRef.current.requestSubmit()
        }
    }
    const [event, updateEvent] = useReducer((prev, next) => ({ ...prev, ...next }), {
        update_obj: {
            name: '',
            data: {},
        },
        work_allocation: {
            m1023_other_secondary_diagnosis: [],
            secondary_diagnostics_qa_feedback: [],
        },
        prev_work_allocation: {
            m1023_other_secondary_diagnosis: [],
            secondary_diagnostics_qa_feedback: [],
        },
    })
    const [debouncedUpdatedObj] = useDebounce(event.update_obj, Constant.DEBOUNCE_DELAY)

    const updateWorkAllocation = async () => {
        try {
            if (!event.update_obj.name) {
                updateEvent({ work_allocation: event.prev_work_allocation })
                return
            }
            if (event?.update_obj?.data && Object.keys(event?.update_obj?.data)?.length == 0) {
                updateEvent({ work_allocation: event.prev_work_allocation })
                return
            }

            if (event.update_obj?.data?.m1023_other_secondary_diagnosis && event.update_obj?.data?.m1023_other_secondary_diagnosis.length > 0) {
                for (const item of event.update_obj.data.m1023_other_secondary_diagnosis) {
                    if (!item.first_diagnostics) {
                        return
                    }
                }
                const alreadyExist = []
                const uniqueICD = []
                for (const item of event.update_obj.data.m1023_other_secondary_diagnosis) {
                    let str = item.first_diagnostics
                    if (!/^[A-Za-z][A-Za-z0-9.-]*$/.test(str)) {
                        toast.error(`Only alphanumeric characters, dashes, and dots are allowed.`)
                        updateEvent({ work_allocation: event.prev_work_allocation })
                        return
                    }
                    if (str.length < 3 || str.length > 8) {
                        toast.error(`Invalid length: "${str}". Must be between 3 and 8 characters.`)
                        updateEvent({ work_allocation: event.prev_work_allocation })
                        return
                    }
                    if (uniqueICD.includes(str)) {
                        alreadyExist.push(str)
                    } else {
                        uniqueICD.push(str)
                    }

                    // if (seen.has(str)) {
                    //     toast.error(`Duplicate found: "${str}"`)
                    //     updateEvent({ work_allocation: event.prev_work_allocation })
                    //     return
                    // }
                    // seen.add(str)
                }
                if (alreadyExist.length) {
                    toast.error(`Duplicate found: "${alreadyExist.join(',')}"`)
                    const updatedDiagnosis = [...event.prev_work_allocation.m1023_other_secondary_diagnosis]
                    uniqueICD.forEach((item) => {
                        const exists = updatedDiagnosis.some((icd_code) => icd_code.first_diagnostics === item)
                        if (!exists) {
                            updatedDiagnosis.push({
                                idx: updatedDiagnosis.length + 1,
                                checkbox: false,
                                first_diagnostics: item,
                            })
                        }
                    })
                    updateEvent({
                        work_allocation: {
                            ...event.work_allocation,
                            m1023_other_secondary_diagnosis: updatedDiagnosis,
                        },
                        update_obj: {
                            name: event.work_allocation.name,
                            data: { m1023_other_secondary_diagnosis: updatedDiagnosis },
                        },
                    })

                    return
                }
            }
            if (event.update_obj?.data?.secondary_diagnostics_qa_feedback && event.update_obj?.data?.secondary_diagnostics_qa_feedback?.length > 0) {
                for (const item of event.work_allocation.secondary_diagnostics_qa_feedback) {
                    if (!item.icd_qa) {
                        return
                    }
                }
                const alreadyExist = []
                const uniqueICD = []
                for (const item of event.update_obj.data.secondary_diagnostics_qa_feedback) {
                    let str = item.icd_qa
                    if (!/^[A-Za-z][A-Za-z0-9.-]*$/.test(str)) {
                        toast.error(`Only alphanumeric characters, dashes, and dots are allowed.`)
                        updateEvent({ work_allocation: event.prev_work_allocation })
                        return
                    }
                    if (str.length < 3 || str.length > 8) {
                        toast.error(`Invalid length: "${str}". Must be between 3 and 8 characters.`)
                        updateEvent({ work_allocation: event.prev_work_allocation })
                        return
                    }
                    if (uniqueICD.includes(str)) {
                        alreadyExist.push(str)
                    } else {
                        uniqueICD.push(str)
                    }
                }
                if (alreadyExist.length) {
                    toast.error(`Duplicate found: "${alreadyExist.join(',')}"`)
                    const updatedDiagnosis = [...event.prev_work_allocation.secondary_diagnostics_qa_feedback]
                    uniqueICD.forEach((item) => {
                        const exists = updatedDiagnosis.some((icd_code) => icd_code.icd_qa === item)
                        if (!exists) {
                            updatedDiagnosis.push({
                                idx: parseInt(updatedDiagnosis.length) + 1,
                                checkbox: false,
                                icd_qa: item,
                                coding_category: '',
                                coding_subcategory: '',
                                qa_comments: '',
                                educational_error: false,
                            })
                        }
                    })
                    updateEvent({
                        work_allocation: {
                            ...event.work_allocation,
                            secondary_diagnostics_qa_feedback: updatedDiagnosis,
                        },
                        update_obj: {
                            name: event.work_allocation.name,
                            data: { secondary_diagnostics_qa_feedback: updatedDiagnosis },
                        },
                    })

                    return
                }
            }
            if (event.update_obj?.data?.mitem_table && event.update_obj?.data?.mitem_table?.length > 0) {
                for (const item of event.update_obj.data.mitem_table) {
                    if (item.clinician_response != '' || item.redroad_coder_response != '' || item.reason_for_change != '') {
                        if (item.clinician_response == '' || item.redroad_coder_response == '' || item.reason_for_change == '') {
                            toast.error('Clinician response ,Coder response and Reason for channge is required for mitem:' + item.name)
                            return
                        }
                        if (item.clinician_response == item.redroad_coder_response) {
                            toast.error('Clinician response ,Coder response should not be same')
                            return
                        }
                    }
                    if (item.clinician_response_qa != '' || item.redroad_qa_response != '' || item.qa_resason_for_change != '') {
                        if (item.clinician_response_qa == '' || item.redroad_qa_response == '' || item.qa_resason_for_change == '') {
                            toast.error('Clinician Response (QA),QA Response and QA Rationale is required for mitem :' + item.name)
                            return
                        }
                        
                    }
                    if(item.redroad_coder_response!=""&&item.redroad_qa_response!=""){
                        if(item.redroad_coder_response==item.redroad_qa_response){
                            toast.error('Coder response and QA response should not be same')
                        }
                    }
                }
            }

            const updated = await WorkAllocationFormService.updateWorkAllocation(event.update_obj)
            updateEvent({ prev_work_allocation: event.work_allocation })
        } catch (err) {
            console.log('error', err)
        }
    }
    useEffect(() => {
        updateWorkAllocation()
    }, [debouncedUpdatedObj])

    async function onSubmit(values) {
        try {
            setIsSaving(true)
            const resp = await UserService.createUser(values)
            if (resp.data.success) {
                setIsSaving(false)
                navigate(`/user/${resp.data.data.name}`)
            }
        } catch (err) {
            setIsSaving(false)
        }
    }

    function onError(errors, e) {
        const errorKeys = Object.keys(errors)
        if (errorKeys.length > 0 && errors[errorKeys[0]]?.message) {
            toast.error(errors[errorKeys[0]].message)
        } else {
            toast.error('Something went wrong.')
        }
    }

    const actionCompleted = () => {}

    const getWorkAllocation = async () => {
        try {
            const work_allocation = await WorkAllocationFormService.getWorkAllocation(id)

            if (work_allocation.data.success) {
                const form_data = {
                    name: work_allocation.data.data.name,
                    chart_status: work_allocation.data.data.chart_status,
                    coder: work_allocation.data.data.coder,
                }
                const action_buttons = await WorkAllocationFormService.getActions(userInfo.email, form_data)

                if (work_allocation.data.data.chart_status == 'Pending') {
                    updateEvent({
                        work_allocation: work_allocation.data.data,
                        prev_work_allocation: work_allocation.data.data,
                        update_obj: {
                            name: work_allocation.data.data.name,
                            data: { chart_status: 'Coding In Progress' },
                        },
                    })
                    return
                }

                updateEvent({
                    work_allocation: work_allocation.data.data,
                    prev_work_allocation: work_allocation.data.data,
                })
            }
        } catch (err) {
            console.log('error', err)
        }
    }

    const handleAction = (type) => {
        console.log('in handle action', type)
        console.log('assessment date', event.work_allocation.no_of_pages)
        if (event.work_allocation.assessment_date == '' || event.work_allocation.assessment_date == null) {
            toast.error('Assessment Date is required')
            return
        } else if (event.work_allocation.gender == '' || event.work_allocation.gender == null) {
            toast.error('Gender is required')
            return
        } else if (event.work_allocation.pdpc == '' || event.work_allocation.pdpc == null) {
            toast.error('PDPC- Huddle Note or Care Coordination Note Present is required')
            return
        } else if (event.work_allocation.no_of_pages == '' || event.work_allocation.no_of_pages == null) {
            toast.error('No of Pages is required')
            return
        } else if (event.work_allocation.m1023_other_secondary_diagnosis.length == 0) {
            toast.error('The inclusion of the ICD code is mandatory.')
            return
        }
        if (event.update_obj?.data?.mitem_table && event.update_obj?.data?.mitem_table?.length > 0) {
            for (const item of event.update_obj.data.mitem_table) {
                if (item.clinician_response != '' || item.redroad_coder_response != '' || item.reason_for_change != '') {
                    if (item.clinician_response == '' || item.redroad_coder_response == '' || item.reason_for_change == '') {
                        toast.error('Clinician response ,Coder response and Reason for channge is required for mitem:' + item.name)
                        return
                    }
                    if (item.clinician_response == item.redroad_coder_response) {
                        toast.error('Clinician response ,Coder response should not be same')
                        return
                    }
                }
                if (item.clinician_response_qa != '' || item.redroad_qa_response != '' || qa_resason_for_change != '') {
                    if (item.clinician_response_qa == '' || item.redroad_qa_response == '' || qa_resason_for_change == '') {
                        toast.error('Clinician Response (QA),QA Response and QA Rationale is required for mitem :' + item.name)
                        return
                    }
                    if (item.clinician_response_qa == item.redroad_qa_response) {
                        toast.error('Clinician Response (QA) and QA Response should not be same')
                    }
                }
            }
        }
    }

    useEffect(() => {
        console.log('in get work allocation useeffect')
        getWorkAllocation()
    }, [])

    const form = useForm()
    return (
        <>
            <HeaderCard>
                <div className="flex justify-end items-center">
                    <div className="flex items-center justify-end gap-2">
                        {/* <Button
                            className="bg-primary-purpal hover:bg-primary-purpal px-4"
                            onClick={handleSaveClick}
                            iconType="Save"
                            disabled={isSaving}
                        >
                            Save {isSaving && <Loader fullScreen={false} />}
                        </Button> */}

                        <ActionButton event={event} updateEvent={updateEvent} handleAction={handleAction} />
                    </div>
                </div>
            </HeaderCard>
            <BodyCard>
                <div className="h-full overflow-hidden">
                    <Form {...form}>
                        <form ref={formRef} onSubmit={form.handleSubmit(onSubmit, onError)} autoComplete="off">
                            <div className="grid grid-cols-5 gap-y-3 gap-x-6 ">
                                <FormField
                                    control={form.control}
                                    name="mr_number"
                                    render={({ field }) => (
                                        <FormItem className="space-y-1">
                                            <FormLabel>MR Number</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="text"
                                                    className=" focus-visible:ring-transparent space-0 mt-0 text-security"
                                                    placeholder="MR Number"
                                                    disabled
                                                    value={event.work_allocation.mr_number}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="patient_name"
                                    render={({ field }) => (
                                        <FormItem className="space-y-1">
                                            <FormLabel>Patient Name</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="text"
                                                    className=" focus-visible:ring-transparent space-0 mt-0 text-security"
                                                    placeholder="Patient Name"
                                                    disabled
                                                    value={event.work_allocation.patient_name}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="assessment_type"
                                    render={({ field }) => (
                                        <FormItem className="space-y-1">
                                            <FormLabel>Assessment Type</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="text"
                                                    className=" focus-visible:ring-transparent space-0 mt-0 text-security"
                                                    placeholder="Assessment Type"
                                                    disabled
                                                    value={event.work_allocation.assessment_type}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="payor_type"
                                    render={({ field }) => (
                                        <FormItem className="space-y-1">
                                            <FormLabel>Payor Type</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="text"
                                                    className=" focus-visible:ring-transparent space-0 mt-0 text-security"
                                                    placeholder="Payor Type"
                                                    disabled
                                                    value={event.work_allocation.assessment_type}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="branch"
                                    render={({ field }) => (
                                        <FormItem className="space-y-1">
                                            <FormLabel>Branch</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="text"
                                                    className=" focus-visible:ring-transparent space-0 mt-0 text-security"
                                                    placeholder="Branch"
                                                    disabled
                                                    value={event.work_allocation.branch}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div>
                                <Tabs
                                    defaultValue={currentTab}
                                    className="relative mr-auto"
                                    style={{ width: true ? 'calc(100vw - 276px)' : 'calc(-60px + 100vw)' }}
                                >
                                    <TabsList className="mt-5 flex flex-wrap justify-around  text-muted-foreground rounded-none border-b-2 bg-white-100 h-fit w-full">
                                        <TabsTrigger className="flex-1 text-center" onClick={() => setCurrentTab('Overview')} value="Overview">
                                            <div className="flex gap-1 justify-center items-center">
                                                <span>Overview</span>
                                            </div>
                                        </TabsTrigger>
                                        <TabsTrigger className="flex-1" onClick={() => setCurrentTab('Coding')} value={'Coding'}>
                                            <div className="flex gap-1 justify-center items-center">
                                                <span>Coding</span>
                                            </div>
                                        </TabsTrigger>
                                        <TabsTrigger className="flex-1" onClick={() => setCurrentTab('OASIS')} value={'OASIS'}>
                                            <div className="flex gap-1 justify-center items-center">
                                                <span>OASIS</span>
                                            </div>
                                        </TabsTrigger>
                                        <TabsTrigger
                                            className="flex-1"
                                            onClick={() => setCurrentTab('Coding Post Problem')}
                                            value={'Coding Post Problem'}
                                        >
                                            <div className="flex gap-1 justify-center items-center">
                                                <span>Coding Post Problem</span>
                                            </div>
                                        </TabsTrigger>
                                        <TabsTrigger className="flex-1" onClick={() => setCurrentTab('Follow-up Mail')} value={'Follow-up Mail'}>
                                            <div className="flex gap-1 justify-center items-center">
                                                <span>Follow-up Mail</span>
                                            </div>
                                        </TabsTrigger>
                                    </TabsList>

                                    <div className="overflow-auto h-[calc(100vh-280px)] overflow-card-scroll">
                                        <TabsContent value="Overview" className="">
                                            <Overview
                                                form={form}
                                                workAllocation={event.work_allocation}
                                                holdReason={holdReason}
                                                technicalIssue={technicalIssue}
                                                chartLockIssue={chartLockIssue}
                                                event={event}
                                                updateEvent={updateEvent}
                                            />
                                        </TabsContent>
                                        <TabsContent value="Coding" className="">
                                            <Coding
                                                form={form}
                                                event={event}
                                                category={category}
                                                subCategory={subCategory}
                                                updateEvent={updateEvent}
                                                workAllocation={event.work_allocation}
                                            />
                                        </TabsContent>
                                        <TabsContent value="OASIS" className="">
                                            <Oasis form={form} event={event} updateEvent={updateEvent} workAllocation={event.work_allocation} />
                                        </TabsContent>
                                        <TabsContent value="Coding Post Problem">
                                            <CodingPostProblem
                                                form={form}
                                                event={event}
                                                updateEvent={updateEvent}
                                                workAllocation={event.work_allocation}
                                            />
                                        </TabsContent>
                                        <TabsContent value="Follow-up Mail">
                                            <Followupmail
                                                form={form}
                                                event={event}
                                                updateEvent={updateEvent}
                                                workAllocation={event.work_allocation}
                                                followupStatus={followupStatus}
                                            />
                                        </TabsContent>
                                    </div>
                                </Tabs>
                            </div>
                        </form>
                    </Form>
                </div>
            </BodyCard>
        </>
    )
}

export default WorkAllocationForm
